/**
 * Count frequency of values by the given key.
 * Empty values are skipped by default (null/undefined/'' or whitespace-only),
 * so charts won't show a blank legend entry.
 *
 * @param data Array of records to aggregate.
 * @param key  Key to group by.
 * @param opts Optional behavior:
 *  - skipEmpty: boolean (default true) — whether to drop empty values.
 *  - treatWhitespaceAsEmpty: boolean (default true) — trim and treat '' as empty.
 *  - toString: (v:any) => string — custom stringifier for non-string values.
 *
 * @example
 * const data = [{ k: 'a' }, { k: 'a' }, { k: '' }, { k: null }];
 * countByKeys(data, 'k')               // => { a: 2 }   (empty values skipped)
 * countByKeys(data, 'k', { skipEmpty:false })
 * // => { a: 2, undefined: 1, '': 1 }
 */
export const countByKeys = <T extends Record<string, any>>(
  data: T[],
  key: keyof T,
  opts: {
    skipEmpty?: boolean;
    treatWhitespaceAsEmpty?: boolean;
    toString?: (v: unknown) => string;
  } = {}
): Record<string, number> => {
  const {
    skipEmpty = true,
    treatWhitespaceAsEmpty = true,
    toString = (v: unknown) => String(v),
  } = opts;

  return data.reduce((acc: Record<string, number>, cur) => {
    const raw = cur[key as string];

    // Decide if value is "empty"
    if (skipEmpty) {
      if (raw === null || raw === undefined) return acc;
      const s = typeof raw === 'string' ? raw : toString(raw);
      if (treatWhitespaceAsEmpty && s.trim() === '') return acc;
      acc[s] = (acc[s] || 0) + 1;
      return acc;
    }

    // Legacy behavior (keep empties)
    const name =
      raw === null || raw === undefined
        ? 'undefined'
        : typeof raw === 'string'
        ? raw
        : toString(raw);
    acc[name] = (acc[name] || 0) + 1;
    return acc;
  }, {});
};
